/**
 * TEMP-H-004: Stage Settings Panel Component
 * 
 * Overview panel showing persona thresholds and settings for a single stage.
 * Provides edit access with role-based permissions and change indicators.
 */

import React, { useState, useMemo } from 'react';
import {
  Settings,
  User,
  Edit3,
  Lock,
  AlertTriangle,
  CheckCircle,
  Hash,
  Clock,
  TrendingUp,
  TrendingDown,
  Minus,
  Monitor
} from 'lucide-react';
import type {
  StageSettingsConfig,
  PersonaThreshold
} from '@/types/stage-settings';
import { PersonaThresholdEditor } from './PersonaThresholdEditor';
import { OrientationOverridesEditor } from './OrientationOverridesEditor';
import { useUpdateStageSettings } from '@/hooks/useUpdateStageSettings';

// CSS-in-JS styles for interactive elements using tokens
const stageSettingsStyles = `
  .stage-settings-edit-btn:hover {
    background-color: color-mix(in srgb, var(--effect-purple) 90%, black) !important;
  }
  
  .stage-settings-edit-btn:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }
`;

// Inject styles once
if (typeof document !== 'undefined' && !document.getElementById('stage-settings-styles')) {
  const styleElement = document.createElement('style');
  styleElement.id = 'stage-settings-styles';
  styleElement.textContent = stageSettingsStyles;
  document.head.appendChild(styleElement);
}

interface StageSettingsPanelProps {
  settings: StageSettingsConfig;
  onRefresh?: () => void;
  showCompactView?: boolean;
  className?: string;
  orientationDefault?: '9:16' | '16:9' | '1:1';
}

interface ThresholdComparisonProps {
  current: PersonaThreshold;
  launch?: PersonaThreshold;
}

const ThresholdComparison: React.FC<ThresholdComparisonProps> = ({ current, launch }) => {
  if (!launch) {
    return (
      <div 
        className="text-xs"
        style={{
          color: 'var(--fg-muted)',
          fontSize: 'var(--font-size-xs)'
        }}
      >
        No launch defaults available
      </div>
    );
  }

  const changes = useMemo(() => {
    const diffs: Array<{ field: string; current: number; launch: number; change: 'increase' | 'decrease' | 'same' }> = [];
    
    // Compare pass minimums
    if (current.passMin !== launch.passMin) {
      diffs.push({
        field: 'Pass Min',
        current: current.passMin,
        launch: launch.passMin,
        change: current.passMin > launch.passMin ? 'increase' : 'decrease'
      });
    }
    
    // Compare fallback minimums
    if (current.fallbackMin !== launch.fallbackMin) {
      diffs.push({
        field: 'Fallback Min', 
        current: current.fallbackMin,
        launch: launch.fallbackMin,
        change: current.fallbackMin > launch.fallbackMin ? 'increase' : 'decrease'
      });
    }
    
    // Compare retry limits
    if (current.retryLimit !== launch.retryLimit) {
      diffs.push({
        field: 'Retry Limit',
        current: current.retryLimit,
        launch: launch.retryLimit,
        change: current.retryLimit > launch.retryLimit ? 'increase' : 'decrease'
      });
    }
    
    return diffs;
  }, [current, launch]);

  if (changes.length === 0) {
    return (
      <div 
        className="flex items-center"
        style={{
          gap: 'var(--space-1)',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--success)'
        }}
      >
        <CheckCircle 
          style={{
            width: 'var(--space-3)',
            height: 'var(--space-3)'
          }}
        />
        Matches launch defaults
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <div 
        className="font-medium"
        style={{
          fontSize: 'var(--font-size-xs)',
          color: 'var(--warning)'
        }}
      >
        Changes from launch:
      </div>
      {changes.map(({ field, current, launch, change }, index) => (
        <div 
          key={index} 
          className="flex items-center"
          style={{
            gap: 'var(--space-2)',
            fontSize: 'var(--font-size-xs)'
          }}
        >
          {change === 'increase' ? (
            <TrendingUp 
              style={{
                width: 'var(--space-3)',
                height: 'var(--space-3)',
                color: 'var(--success)'
              }}
            />
          ) : change === 'decrease' ? (
            <TrendingDown 
              style={{
                width: 'var(--space-3)',
                height: 'var(--space-3)',
                color: 'var(--danger)'
              }}
            />
          ) : (
            <Minus 
              style={{
                width: 'var(--space-3)',
                height: 'var(--space-3)',
                color: 'var(--fg-muted)'
              }}
            />
          )}
          <span style={{ color: 'var(--fg-secondary)' }}>
            {field}: {(current * 100).toFixed(0)}% 
            <span style={{ color: 'var(--fg-muted)' }}>
              (was {(launch * 100).toFixed(0)}%)
            </span>
          </span>
        </div>
      ))}
    </div>
  );
};

export function StageSettingsPanel({
  settings,
  onRefresh,
  showCompactView = false,
  className = '',
  orientationDefault = '9:16'
}: StageSettingsPanelProps) {
  const [showEditor, setShowEditor] = useState(false);
  const [selectedPersona, setSelectedPersona] = useState<string | null>(null);
  const [showOrientationEditor, setShowOrientationEditor] = useState(false);

  const {
    updateSettings,
    isLoading: isUpdating,
    error: updateError,
    reset: resetUpdate
  } = useUpdateStageSettings(settings.executionId, settings.stage, {
    onSuccess: () => {
      setShowEditor(false);
      setSelectedPersona(null);
      onRefresh?.();
    }
  });

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // TODO: Use permission icons in UI
  // const getPermissionIcon = (permissions: SettingsPermissionCheck) => {
  //   if (permissions.canEditAdvanced) {
  //     return <Edit3 style={{ width: 'var(--space-4)', height: 'var(--space-4)', color: 'var(--success)' }} />;
  //   } else if (permissions.canEdit) {
  //     return <Edit3 style={{ width: 'var(--space-4)', height: 'var(--space-4)', color: 'var(--warning)' }} />;
  //   } else if (permissions.canView) {
  //     return <Eye style={{ width: 'var(--space-4)', height: 'var(--space-4)', color: 'var(--info)' }} />;
  //   } else {
  //     return <Lock style={{ width: 'var(--space-4)', height: 'var(--space-4)', color: 'var(--danger)' }} />;
  //   }
  // };

  const handleEditClick = () => {
    if (!settings.isEditable) return;
    setSelectedPersona(settings.activePersona.personaSlug);
    setShowEditor(true);
  };

  // Show Persona Threshold Editor
  if (showEditor && selectedPersona) {
    return (
      <div 
        className={className}
        style={{
          backgroundColor: 'var(--bg-elevated)',
          borderRadius: 'var(--radius-lg)',
          border: `1px solid var(--border-default)`,
          padding: 'var(--space-8)'
        }}
      >
        <PersonaThresholdEditor
          persona={settings.activePersona}
          executionId={settings.executionId}
          stage={settings.stage}
          isEditable={settings.isEditable}
          isLoading={isUpdating}
          onSave={async (updates) => { await updateSettings(updates); }}
          onCancel={() => {
            setShowEditor(false);
            setSelectedPersona(null);
            resetUpdate();
          }}
        />
      </div>
    );
  }

  // Show Orientation Overrides Editor (only for frames and videos stages)
  if (showOrientationEditor && (settings.stage === 'frames' || settings.stage === 'videos')) {
    return (
      <div 
        className={className}
        style={{
          backgroundColor: 'var(--bg-elevated)',
          borderRadius: 'var(--radius-lg)',
          border: `1px solid var(--border-default)`,
          padding: 'var(--space-8)'
        }}
      >
        <OrientationOverridesEditor
          executionId={settings.executionId}
          stage={settings.stage}
          orientationDefault={orientationDefault}
          onSave={() => {
            setShowOrientationEditor(false);
            onRefresh?.();
          }}
        />
        <div 
          className="flex justify-end"
          style={{ marginTop: 'var(--space-5)' }}
        >
          <button
            onClick={() => setShowOrientationEditor(false)}
            className="btn-secondary"
          >
            Back to Settings
          </button>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={className}
      style={{
        backgroundColor: 'var(--bg-elevated)',
        borderRadius: 'var(--radius-lg)',
        border: `1px solid var(--border-default)`
      }}
    >
      {/* Header */}
      <div 
        style={{
          padding: 'var(--space-5)',
          borderBottom: `1px solid var(--border-default)`
        }}
      >
        <div className="flex items-center justify-between">
          <div 
            className="flex items-center"
            style={{ gap: 'var(--space-3)' }}
          >
            <div 
              className="flex items-center justify-center"
              style={{
                width: 'var(--space-8)',
                height: 'var(--space-8)',
                backgroundColor: 'color-mix(in srgb, var(--effect-purple) 20%, transparent)',
                borderRadius: 'var(--radius-lg)'
              }}
            >
              <Settings 
                style={{
                  width: 'var(--space-4)',
                  height: 'var(--space-4)',
                  color: 'var(--effect-purple)'
                }}
              />
            </div>
            <div>
              <h3 
                className="font-semibold capitalize"
                style={{
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--fg-primary)'
                }}
              >
                {settings.stage} Settings
              </h3>
              <p 
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--fg-muted)'
                }}
              >
                v{settings.version} • Updated {formatTimestamp(settings.updatedAt)}
              </p>
            </div>
          </div>

          <div 
            className="flex items-center"
            style={{ gap: 'var(--space-2)' }}
          >
            {settings.overriddenFromLaunch && (
              <div 
                style={{
                  padding: 'var(--space-1) var(--space-2)',
                  fontSize: 'var(--font-size-xs)',
                  backgroundColor: 'color-mix(in srgb, var(--warning) 20%, transparent)',
                  color: 'var(--warning)',
                  borderRadius: 'var(--radius-md)',
                  border: `1px solid color-mix(in srgb, var(--warning) 30%, transparent)`
                }}
              >
                Modified
              </div>
            )}
            
            {/* Orientation Overrides Button - only for frames/videos stages */}
            {settings.isEditable && (settings.stage === 'frames' || settings.stage === 'videos') && (
              <button
                onClick={() => setShowOrientationEditor(true)}
                className="btn-primary flex items-center"
                style={{
                  gap: 'var(--space-1)',
                  fontSize: 'var(--font-size-xs)'
                }}
              >
                <Monitor className="w-3 h-3" />
                Orientation
              </button>
            )}
            
            {settings.isEditable ? (
              <button
                onClick={handleEditClick}
                className="flex items-center stage-settings-edit-btn"
                style={{
                  gap: 'var(--space-1)',
                  padding: 'var(--space-3) var(--space-3)',
                  fontSize: 'var(--font-size-xs)',
                  backgroundColor: 'var(--effect-purple)',
                  color: 'var(--fg-on-accent)',
                  borderRadius: 'var(--radius-lg)',
                  border: 'none',
                  transition: `background-color var(--motion-duration-fast) var(--motion-ease-standard)`,
                  cursor: 'pointer'
                }}
              >
                <Edit3 
                  style={{
                    width: 'var(--space-3)',
                    height: 'var(--space-3)'
                  }}
                />
                Edit Thresholds
              </button>
            ) : (
              <div 
                className="flex items-center"
                style={{
                  gap: 'var(--space-1)',
                  padding: 'var(--space-3) var(--space-3)',
                  fontSize: 'var(--font-size-xs)',
                  backgroundColor: 'var(--bg-subtle)',
                  color: 'var(--fg-muted)',
                  borderRadius: 'var(--radius-lg)'
                }}
              >
                <Lock 
                  style={{
                    width: 'var(--space-3)',
                    height: 'var(--space-3)'
                  }}
                />
                View Only
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div 
        style={{
          padding: 'var(--space-5)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-5)'
        }}
      >
        {/* Active Persona */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          <h4 
            className="font-medium flex items-center"
            style={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--fg-secondary)',
              gap: 'var(--space-2)'
            }}
          >
            <User 
              style={{
                width: 'var(--space-4)',
                height: 'var(--space-4)'
              }}
            />
            Active Persona
          </h4>
          
          <div 
            style={{
              backgroundColor: 'var(--bg-subtle)',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--space-5)'
            }}
          >
            <div 
              className="flex items-start justify-between"
              style={{ marginBottom: 'var(--space-3)' }}
            >
              <div>
                <div 
                  className="font-medium"
                  style={{ color: 'var(--fg-primary)' }}
                >
                  {settings.activePersona.personaName}
                </div>
                <div 
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--fg-muted)'
                  }}
                >
                  {settings.activePersona.personaRole}
                </div>
              </div>
              <div 
                className="font-mono"
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--fg-disabled)',
                  fontFamily: 'var(--font-family-mono)'
                }}
              >
                #{settings.activePersona.hash.slice(0, 8)}
              </div>
            </div>

            {/* Threshold Display */}
            <div 
              className="grid grid-cols-3"
              style={{
                gap: 'var(--space-5)',
                marginBottom: 'var(--space-5)'
              }}
            >
              <div className="text-center">
                <div 
                  className="font-mono"
                  style={{
                    fontSize: 'var(--font-size-lg)',
                    fontFamily: 'var(--font-family-mono)',
                    color: 'var(--success)'
                  }}
                >
                  {(settings.activePersona.passMin * 100).toFixed(0)}%
                </div>
                <div 
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--fg-disabled)'
                  }}
                >
                  Pass Min
                </div>
              </div>
              <div className="text-center">
                <div 
                  className="font-mono"
                  style={{
                    fontSize: 'var(--font-size-lg)',
                    fontFamily: 'var(--font-family-mono)',
                    color: 'var(--warning)'
                  }}
                >
                  {(settings.activePersona.fallbackMin * 100).toFixed(0)}%
                </div>
                <div 
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--fg-disabled)'
                  }}
                >
                  Fallback Min
                </div>
              </div>
              <div className="text-center">
                <div 
                  className="font-mono"
                  style={{
                    fontSize: 'var(--font-size-lg)',
                    fontFamily: 'var(--font-family-mono)',
                    color: 'var(--info)'
                  }}
                >
                  {settings.activePersona.retryLimit}
                </div>
                <div 
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--fg-disabled)'
                  }}
                >
                  Retry Limit
                </div>
              </div>
            </div>

            {/* Categories (if any) */}
            {Object.keys(settings.activePersona.categoryThresholds || {}).length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                <div 
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--fg-muted)'
                  }}
                >
                  Category Thresholds:
                </div>
                <div 
                  className="grid grid-cols-2"
                  style={{ gap: 'var(--space-2)' }}
                >
                  {Object.entries(settings.activePersona.categoryThresholds).map(([key, threshold]) => (
                    <div 
                      key={key} 
                      className="flex justify-between"
                      style={{ fontSize: 'var(--font-size-xs)' }}
                    >
                      <span 
                        className="capitalize"
                        style={{ color: 'var(--fg-secondary)' }}
                      >
                        {key.replace(/_/g, ' ')}
                      </span>
                      <span 
                        className="font-mono"
                        style={{
                          color: 'var(--fg-muted)',
                          fontFamily: 'var(--font-family-mono)'
                        }}
                      >
                        {(threshold * 100).toFixed(0)}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Comparison with Launch Defaults */}
        {!showCompactView && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
            <h4 
              className="font-medium flex items-center"
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--fg-secondary)',
                gap: 'var(--space-2)'
              }}
            >
              <Hash 
                style={{
                  width: 'var(--space-4)',
                  height: 'var(--space-4)'
                }}
              />
              Change Summary
            </h4>
            
            <div 
              style={{
                backgroundColor: 'var(--bg-subtle)',
                borderRadius: 'var(--radius-lg)',
                padding: 'var(--space-5)'
              }}
            >
              <ThresholdComparison 
                current={settings.activePersona}
                launch={settings.launchDefaults}
              />
            </div>
          </div>
        )}

        {/* Alternative Personas */}
        {!showCompactView && settings.availablePersonas.length > 1 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
            <h4 
              className="font-medium"
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--fg-secondary)'
              }}
            >
              Alternative Personas ({settings.availablePersonas.length - 1})
            </h4>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              {settings.availablePersonas
                .filter(p => p.personaSlug !== settings.activePersona.personaSlug)
                .slice(0, 3) // Show max 3 alternatives
                .map(persona => (
                <div 
                  key={persona.personaSlug} 
                  style={{
                    backgroundColor: 'color-mix(in srgb, var(--bg-subtle) 30%, transparent)',
                    borderRadius: 'var(--radius-md)',
                    padding: 'var(--space-3)'
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div 
                        style={{
                          fontSize: 'var(--font-size-sm)',
                          color: 'var(--fg-primary)'
                        }}
                      >
                        {persona.personaName}
                      </div>
                      <div 
                        style={{
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--fg-disabled)'
                        }}
                      >
                        {persona.personaRole}
                      </div>
                    </div>
                    <div 
                      style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--fg-muted)',
                        display: 'flex',
                        gap: 'var(--space-2)'
                      }}
                    >
                      <span>{(persona.passMin * 100).toFixed(0)}%</span>
                      <span>/</span>
                      <span>{(persona.fallbackMin * 100).toFixed(0)}%</span>
                    </div>
                  </div>
                </div>
              ))}
              
              {settings.availablePersonas.length > 4 && (
                <div 
                  className="text-center"
                  style={{ padding: 'var(--space-2) 0' }}
                >
                  <span 
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--fg-disabled)'
                    }}
                  >
                    +{settings.availablePersonas.length - 4} more personas available
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Update Info */}
        {settings.updatedBy && (
          <div 
            style={{
              paddingTop: 'var(--space-3)',
              borderTop: `1px solid var(--border-default)`
            }}
          >
            <div 
              className="flex items-center"
              style={{
                gap: 'var(--space-2)',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--fg-disabled)'
              }}
            >
              <Clock 
                style={{
                  width: 'var(--space-3)',
                  height: 'var(--space-3)'
                }}
              />
              Last modified by {settings.updatedBy} on {formatTimestamp(settings.updatedAt)}
            </div>
          </div>
        )}
      </div>

      {/* Update Error Display */}
      {updateError && (
        <div 
          style={{
            padding: 'var(--space-5)',
            borderTop: `1px solid var(--border-default)`
          }}
        >
          <div 
            className="flex items-start"
            style={{
              gap: 'var(--space-2)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--danger)'
            }}
          >
            <AlertTriangle 
              className="flex-shrink-0"
              style={{
                width: 'var(--space-4)',
                height: 'var(--space-4)',
                marginTop: 'calc(var(--space-1) / 2)'
              }}
            />
            <div>
              <div className="font-medium">Update Failed</div>
              <div 
                style={{
                  fontSize: 'var(--font-size-xs)',
                  marginTop: 'var(--space-1)'
                }}
              >
                {updateError.message}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}